import React, { useState, useRef, useEffect } from 'react';
import Button from '../Common/Button';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import {
  composerDraftToShow,
  forgetComposerDraft,
  readComposerDraft,
  rememberComposerDraft,
} from '../../utils/composerDraft';
import { useRoomChatDraft } from './roomChatDraft';
import './Chat.css';

/** True when focus should stay where the writer put it. */
function shouldLeaveComposerFocus(root) {
  const active = document.activeElement;
  if (active && active.closest('[role="dialog"], [role="alertdialog"], dialog')) return true;
  if (document.querySelector('[role="dialog"][aria-modal="true"], [role="alertdialog"][aria-modal="true"]')) {
    return true;
  }
  if (!active || active === document.body || active === document.documentElement) return false;
  if (root && root.contains(active)) return false;
  return true;
}

const MessageInput = ({
  onSend,
  disabled,
  readOnly = false,
  placeholder,
  onSettingsClick,
  showConfigure = true,
  estimate = null,
  sessionId = null,
  echoText = '',
  focusToken = 0,
  restoreWhenEnabled = false,
  onComposerFocusRestored = null,
}) => {
  const roomDraft = useRoomChatDraft();
  const roomDraftKey = roomDraft?.key || '';
  const [content, setContent] = useState(() => (roomDraftKey ? (roomDraft.text || '') : ''));
  const roomDraftKeyRef = useRef(roomDraftKey);
  if (roomDraftKey !== roomDraftKeyRef.current) {
    roomDraftKeyRef.current = roomDraftKey;
    setContent(roomDraft?.text || '');
  }
  const contentRef = useRef(content);
  contentRef.current = content;
  const textareaRef = useRef(null);
  const rootRef = useRef(null);
  const restoredRef = useRef(onComposerFocusRestored);
  restoredRef.current = onComposerFocusRestored;
  const breakpoint = useBreakpoint();

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (breakpoint === 'mobile') return;
      if (!e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    }
  };

  const clearDraft = () => {
    setContent('');
    if (roomDraftKey) roomDraft.setText('');
    forgetComposerDraft(sessionId);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  useEffect(() => {
    if (roomDraftKey) return;
    if (!sessionId) return;
    const saved = readComposerDraft(sessionId);
    const show = composerDraftToShow(saved, echoText);
    if (saved && !show) {
      forgetComposerDraft(sessionId);
      setContent((current) => (current === saved ? '' : current));
      return;
    }
    if (show) setContent((current) => current || show);
  }, [sessionId, echoText, roomDraftKey]);

  const handleSend = () => {
    const trimmedContent = content.trim();
    if (trimmedContent && !disabled) {
      // The sent text clears now. A failed send puts it back only into an
      // empty box, so text typed after Send is never overwritten.
      const restore = () => {
        if (contentRef.current) return;
        setContent(trimmedContent);
        if (roomDraftKey) roomDraft.setText(trimmedContent);
        else rememberComposerDraft(sessionId, trimmedContent);
      };
      const accepted = onSend(trimmedContent, restore);
      if (accepted !== false) clearDraft();
      textareaRef.current?.focus();
    }
  };

  const handleChange = (e) => {
    setContent(e.target.value);
    if (roomDraftKey) roomDraft.setText(e.target.value);
    else rememberComposerDraft(sessionId, e.target.value);
    // Auto-grow textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  };

  useEffect(() => {
    // Reset height when disabled changes
    if (textareaRef.current && !disabled) {
      textareaRef.current.style.height = 'auto';
    }
  }, [disabled]);

  useEffect(() => {
    if (!focusToken) return;
    textareaRef.current?.focus();
  }, [focusToken]);

  // After a send the writer made, put the caret back once the box
  // re-enables. A mount with the flag off does not focus. A dialog,
  // the model picker, or another field keeps the focus it has.
  useEffect(() => {
    if (!restoreWhenEnabled) return undefined;
    if (readOnly) {
      restoredRef.current?.();
      return undefined;
    }
    if (disabled) return undefined;
    const timer = window.setTimeout(() => {
      const node = textareaRef.current;
      if (!node || node.disabled) return;
      if (!shouldLeaveComposerFocus(rootRef.current)) node.focus();
      restoredRef.current?.();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [restoreWhenEnabled, disabled, readOnly]);

  // If in read-only mode, show a special UI
  if (readOnly) {
    return (
      <div className="message-input message-input--readonly">
        <div className="message-input__readonly-message">
          {placeholder || (showConfigure === false ? '' : 'Configure API key to start chatting')}
        </div>
        {showConfigure !== false && onSettingsClick && (
          <Button
            variant="primary"
            size="medium"
            onClick={onSettingsClick}
            className="message-input__settings-button"
          >
            Configure
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="message-input" ref={rootRef}>
      <textarea
        ref={textareaRef}
        className="message-input__textarea"
        placeholder={placeholder || (breakpoint === 'mobile' ? "Type your message..." : "Type your message... (Shift+Enter for new line)")}
        value={content}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        rows={1}
      />
      <div className="message-input__send-row">
        {estimate}
        <Button
          variant="primary"
          size="medium"
          onClick={handleSend}
          disabled={disabled || !content.trim()}
          className="message-input__button"
        >
          Send
        </Button>
      </div>
    </div>
  );
};

export default MessageInput;
